import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiXMark,
  HiEnvelope,
  HiPhone,
  HiUserGroup,
  HiAcademicCap,
  HiShieldCheck,
  HiMapPin,
  HiFlag,
  HiDocumentText,
  HiBriefcase,
  HiLink,
  HiClipboardDocumentList,
  HiPlus,
} from 'react-icons/hi2';
import ReportSubmissionForm from './ReportSubmissionForm';
import ClientReportsViewer from './ClientReportsViewer';
import DocumentBankPage from '../../components/DocumentBank/DocumentBankPage';
import ClientComments from './ClientComments';

const ClientProfilePopover = ({ isOpen, onClose, client, anchorEl, currentUser, userRole }) => {
  const popoverRef = useRef(null);
  const [showReportForm, setShowReportForm] = useState(false);
  const [showReportsViewer, setShowReportsViewer] = useState(false);
  const [showDocBank, setShowDocBank] = useState(false);

  useEffect(() => {
    const handleClickOutside = event => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = event => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }
    const openDb = e => {
      const target = e?.detail?.client;
      if (target && client && target.id === client.id) {
        setShowDocBank(true);
      }
    };
    window.addEventListener('open-document-bank', openDb);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('open-document-bank', openDb);
    };
  }, [isOpen, onClose]);

  if (!client || !isOpen) return null;

  const getPopoverPosition = () => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const popoverWidth = 800;
    const popoverHeight = 600;
    const left = Math.max(16, (viewportWidth - popoverWidth) / 2);
    const top = Math.max(16, (viewportHeight - popoverHeight) / 2);

    return { top, left };
  };

  const formatDate = date => {
    if (!date) return 'Not available';
    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ duration: 0.15 }}
          className="fixed z-50 rounded-xl border border-gray-200 bg-white shadow-2xl ring-1 ring-black/5 backdrop-blur-sm"
          ref={popoverRef}
          style={{
            ...getPopoverPosition(),
            width: 'min(800px, calc(100vw - 32px))',
            maxHeight: 'calc(100vh - 32px)',
          }}
        >
          <motion.div
            className="relative overflow-hidden rounded-t-xl bg-gradient-to-br from-blue-50 to-indigo-50 px-4 py-6 md:px-8 md:py-8"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="relative">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-xl font-bold text-white shadow-lg md:h-20 md:w-20 md:text-2xl">
                    {(client.firstname || client.email || client.id)?.[0]?.toUpperCase() || 'C'}
                  </div>
                  <motion.div
                    className={`absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-2 border-white ${
                      client.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                    }`}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3, type: 'spring', stiffness: 500 }}
                  ></motion.div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-black md:text-2xl">
                    {client.firstname || ''} {client.lastname || ''}
                  </h3>
                  <p className="text-base text-gray-600 md:text-lg">
                    {client.organization || 'No organization'}
                  </p>
                  {client.email && <p className="text-sm text-gray-500">{client.email}</p>}
                  <div className="mt-2 flex items-center gap-3">
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
                        client.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {client.status || 'Unknown'}
                    </span>
                    {client.program && (
                      <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800">
                        {client.program}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-full p-3 text-gray-400 transition-all duration-200 hover:bg-white/60 hover:text-gray-700 hover:shadow-sm"
              >
                <HiXMark className="h-6 w-6" />
              </button>
            </div>
          </motion.div>

          <motion.div
            className="max-h-[60vh] overflow-y-auto p-4 md:max-h-[500px] md:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <div className="space-y-8">
              <div className="grid grid-cols-1 gap-6 sm:gap-8 lg:grid-cols-2">
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100">
                      <HiEnvelope className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-500">Email Address</p>
                      <p className="text-base font-semibold text-black">
                        {client.email || 'Not provided'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-100">
                      <HiPhone className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-500">Phone Number</p>
                      <p className="text-base font-semibold text-black">
                        {client.phone || 'Not provided'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-100">
                      <HiMapPin className="h-5 w-5 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-500">Location</p>
                      <p className="text-base font-semibold text-black">
                        {client.location || 'Not specified'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-100">
                      <HiFlag className="h-5 w-5 text-orange-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-500">Country</p>
                      <p className="text-base font-semibold text-black">
                        {client.country || 'Not specified'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {showDocBank &&
                (userRole === 'admin' || userRole === 'moderator' || userRole === 'coach') && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="h-[90vh] w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl">
                      <div className="flex items-center justify-between border-b px-4 py-3">
                        <div className="text-sm font-semibold">
                          Document Bank — {client?.firstname} {client?.lastname}
                        </div>
                        <button
                          onClick={() => setShowDocBank(false)}
                          className="rounded bg-gray-100 px-3 py-1 text-sm"
                        >
                          Close
                        </button>
                      </div>
                      <div className="h-[calc(90vh-48px)] overflow-auto">
                        <DocumentBankPage forClientId={client?.id} forClientEmail={client?.email} />
                      </div>
                    </div>
                  </div>
                )}
              <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-6">
                <h4 className="mb-4 flex items-center gap-3 text-base font-semibold text-gray-900">
                  <HiBriefcase className="h-5 w-5 text-blue-600" />
                  Business Details
                </h4>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Business Stage</p>
                    <p className="text-base font-semibold text-black">
                      {client.businessStage || 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Cohort Group</p>
                    <p className="text-base font-semibold text-black">
                      {client.corhotGroup || 'Not assigned'}
                    </p>
                  </div>
                </div>
              </div>

              {client.bio && (
                <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-6">
                  <h4 className="mb-3 flex items-center gap-3 text-base font-semibold text-gray-900">
                    <HiDocumentText className="h-5 w-5 text-blue-600" />
                    Bio
                  </h4>
                  <p className="text-base leading-relaxed text-gray-700">{client.bio}</p>
                </div>
              )}

              <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-6">
                <h4 className="mb-4 flex items-center gap-3 text-base font-semibold text-gray-900">
                  <HiUserGroup className="h-5 w-5 text-blue-600" />
                  Assignments
                </h4>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100">
                      <HiAcademicCap className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-500">Moderator</p>
                      <p className="text-base font-semibold text-black">
                        {client.assigned || 'Not assigned'}
                      </p>
                      {client.assignedEmail && (
                        <p className="text-sm text-gray-500">{client.assignedEmail}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-100">
                      <HiUserGroup className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-500">Coach</p>
                      <p className="text-base font-semibold text-black">
                        {client.assignedCoachName || 'Not assigned'}
                      </p>
                      {client.assignedCoachEmail && (
                        <p className="text-sm text-gray-500">{client.assignedCoachEmail}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {client.profileLink && (
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-100">
                    <HiLink className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500">Profile Link</p>
                    <a
                      href={client.profileLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base font-medium text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      View Profile
                    </a>
                  </div>
                </div>
              )}

              <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-6">
                <h4 className="mb-4 flex items-center gap-3 text-base font-semibold text-gray-900">
                  <HiShieldCheck className="h-5 w-5 text-blue-600" />
                  Account Info
                </h4>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-sm font-medium text-gray-500">User ID</p>
                    <p className="font-mono text-sm text-gray-600">
                      {client.userId || client.id || 'Not available'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Created</p>
                    <p className="text-base text-black">{formatDate(client.createdAt)}</p>
                  </div>
                  {client.updatedAt && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Last Updated</p>
                      <p className="text-base text-black">{formatDate(client.updatedAt)}</p>
                    </div>
                  )}
                </div>
              </div>

              {['admin', 'moderator', 'coach'].includes(userRole) && (
                <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-6">
                  <h4 className="mb-4 flex items-center gap-3 text-base font-semibold text-gray-900">
                    <HiClipboardDocumentList className="h-5 w-5 text-blue-600" />
                    Private Reports
                  </h4>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setShowReportForm(true)}
                      className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                    >
                      <HiPlus className="h-4 w-4" />
                      Submit Report
                    </button>
                    <button
                      onClick={() => setShowReportsViewer(true)}
                      className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      <HiDocumentText className="h-4 w-4" />
                      View Reports
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    These reports are confidential and only visible to administrators, business
                    advisors, and coaches.
                  </p>
                </div>
              )}

              <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-6">
                <ClientComments client={client} currentUser={currentUser} userRole={userRole} />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      <ReportSubmissionForm
        isOpen={showReportForm}
        onClose={() => setShowReportForm(false)}
        client={client}
        currentUser={currentUser}
        userRole={userRole}
      />

      <ClientReportsViewer
        isOpen={showReportsViewer}
        onClose={() => setShowReportsViewer(false)}
        client={client}
        userRole={userRole}
      />
    </AnimatePresence>
  );
};

export default ClientProfilePopover;
